import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { fetchRequestDetail } from '../../lib/consoleRequestDetail';
import { labelFor, CUSTOMIZATION_CHIPS } from '../../lib/requestFormOptions';
// The trader's terms as the supplier reads them — the same builder /f/, /rfq,
// the supplier dashboard and the app render (2026-09-28). It replaced this
// page's own quantity / timeframe / destination rows, which had drifted into
// separate wording ("Delivery" here meant the timeframe).
import { supplierTermRows } from '../../lib/supplierRequestTerms';
// The trilingual map the supplier dashboard and RequestDetail already use for
// offers.status, plus its tones. No second map on this page.
import { OFFER_STATUS, offerStatusTone } from '../../lib/supplierDashboardConstants';
import { displayCategoryForCode } from '../../lib/factoryCategories';
import { offerVisibilityChip } from '../../lib/offerVisibility';
// bucketOf is imported rather than reimplemented: it already encodes "is this
// supplier's file reviewable", is paired with fetchReadyCount for the sidebar
// badge, and a fourth expression of that test is exactly the drift CLAUDE.md
// warns about.
import { bucketOf } from '../../lib/consoleVerification';
import DistributePanel from './DistributePanel';
// The ONE map for what a requests.status is CALLED and what colour it is.
// This screen printed the RAW column instead — see the badge below.
import { requestStatusLabel, requestStatusTone } from '../../lib/requestStatus';
// The SAME stage bar the trader sees, not a rebuild of it. Extracted from
// DashboardBuyer on 2026-09-22 for exactly this: an admin looking at a
// request should be looking at what the trader is looking at.
import { RequestTimeline } from '../../lib/requestTimeline';
import InviteModal from './InviteModal';
import DeadlineEditor from './DeadlineEditor';
import PublicLinkPanel from './PublicLinkPanel';
import RequestEditPanel from './RequestEditPanel';

// Which column a masked original came from, in the admin's language. Keys are
// the `field` values mask_request_text() and mask_request_customization_text()
// write (20261011223741).
const MASKED_FIELD_LABEL = {
  title_ar: { ar: 'العنوان (عربي)', en: 'Title (Arabic)' },
  title_en: { ar: 'العنوان (إنجليزي)', en: 'Title (English)' },
  title_zh: { ar: 'العنوان (صيني)', en: 'Title (Chinese)' },
  product_name: { ar: 'اسم المنتج', en: 'Product name' },
  description: { ar: 'الوصف', en: 'Description' },
  description_ar: { ar: 'الوصف (عربي)', en: 'Description (Arabic)' },
  description_en: { ar: 'الوصف (إنجليزي)', en: 'Description (English)' },
  description_zh: { ar: 'الوصف (صيني)', en: 'Description (Chinese)' },
  'customization.customization_details': { ar: 'تفاصيل التخصيص', en: 'Customization details' },
  'customization.additional_notes': { ar: 'ملاحظات إضافية', en: 'Additional notes' },
};

// ── One RFQ, opened ────────────────────────────────────────────────────────
//
// The console could list requests and distribute them, but never OPEN one. An
// admin deciding who should see a request was deciding about something he could
// not read — the board showed a title and a status, and the only way to see what
// a factory would actually receive was to hold an invite link and open /f/.
//
// Three blocks, in the order the decision is made:
//   1. what the factory reads
//   2. who already has it
//   3. who else should get it  (the existing distribution panel, unchanged)
//
// Then the deadline, which gates the fifth: the public page hides the "Respond
// by" row without one, and nothing is shared without a deadline.
//
// The fifth — a public link to post outside the platform — was a deliberately
// locked box until 2026-09-18. Both conditions its note named are now met: M2
// (identity matching on claim) shipped, and request_public_links landed in
// migration 20260918174609. It is now PublicLinkPanel.

const nf = (v) => (v && v !== 'not_found' ? String(v).trim() : '');
const fmt = (iso, isAr) => (iso ? new Date(iso).toLocaleString(isAr ? 'ar' : 'en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

export default function ConsoleRequestDetail({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [toast, setToast] = useState('');
  const [invite, setInvite] = useState(null);

  const load = useCallback(() => {
    setErr('');
    fetchRequestDetail(id)
      .then((x) => setD(x || { missing: true }))
      .catch((e) => { console.error('[ConsoleRequestDetail] load failed:', e?.message || e); setErr(e?.message || 'load failed'); });
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 2200); };

  // labelFor takes ONE option object and a language, not a list and a key — so
  // the lookup happens here. Falling back to the raw key rather than to blank:
  // an unrecognised value is a data question, and hiding it answers nothing.
  const chipLabel = (list, key) => {
    if (!key) return null;
    const o = list.find((x) => x.key === key);
    return o ? labelFor(o, lang) : String(key);
  };

  const r = d?.request;
  const fv = d?.factoryView;
  // The request carries a RAW code (electronics, bedroom_furniture). Factories
  // are filtered by a DISPLAY category, which is a group of codes — so the two
  // are bridged here rather than assumed equal. bedroom_furniture, for one, is
  // not a factory code at all; it lives inside the furniture group.
  const cat = r?.category ? displayCategoryForCode(r.category) : null;
  const title = fv?.title || (isAr ? 'طلب بلا عنوان' : 'Untitled request');

  const Row = ({ k, v }) => (v ? (
    <div style={{ display: 'flex', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--ac-line)' }}>
      <span style={{ minWidth: 130, fontSize: 12, color: 'var(--ac-faint)' }}>{k}</span>
      <span style={{ fontSize: 13, color: 'var(--ac-ink)', flex: 1 }}>{v}</span>
    </div>
  ) : null);

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="quotations">
      <div className="ac-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        <div className="ac-page-head">
          <div>
            <p className="ac-sub" style={{ margin: 0 }}>
              {nf(r?.request_ref) || (isAr ? 'طلب' : 'Request')}
              {r?.created_at ? ` · ${fmt(r.created_at, isAr)}` : ''}
            </p>
            {/* THE REQUEST'S OWN STATUS, for every request and not only the
                interesting ones. This screen had none at all: an admin could
                open a request and read its brief, its invites and its offers
                without ever learning whether it was still live. It had to be
                inferred from which panels happened to render.

                ⚠️ THIS PRINTED THE RAW COLUMN until 2026-09-22. Only 'cancelled'
                had a translation here; every other status rendered as the
                database string, so an admin read "closed" — an English
                identifier — in an Arabic screen, on the very request whose
                trader had just accepted an offer.

                Both the word and the colour now come from
                src/lib/requestStatus.js, the same map the trader's dashboard
                and RequestDetail read, so one event reads alike everywhere.
                'closed' is GREEN there: it is an accepted offer, not an
                ending. Do not reintroduce a local string here — a second map
                is what produced the raw value in the first place. */}
            {nf(r?.status) && (
              <span className={`ac-badge ${requestStatusTone(r.status)}`}
                style={{ marginTop: 6, marginInlineEnd: 6 }}>
                <span className="dot" />
                {requestStatusLabel(r.status, lang)}
              </span>
            )}
            <h1 className="ac-h1">{d ? title : '…'}</h1>
            {/* The category belongs WITH the request, not in a filter somewhere:
                it is what tells an admin which factories this is for, and what
                a stranger reading a shared post needs to know it concerns them. */}
            {r?.category && (
              <span className="ac-badge info" style={{ marginTop: 6 }}>
                <span className="dot" />
                {cat ? (cat.label?.[lang] || cat.label?.en || r.category) : r.category}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {/* Document generator, with this request already chosen. Linking
                only — the tool never writes to the request. */}
            <button className="ac-btn ac-btn-sm" onClick={() => nav(`/admin2/docs/new?request=${id}&kind=quotation`)}>
              {isAr ? 'عرض سعر للعميل' : 'Client quotation'}
            </button>
            <button className="ac-btn ac-btn-sm" onClick={() => nav(`/admin2/docs/new?request=${id}&kind=rfq`)}>
              {isAr ? 'طلب تسعير للمصنع (RFQ)' : 'Supplier RFQ'}
            </button>
            <button className="ac-btn" onClick={() => nav('/admin2/quotations')}>
              {isAr ? '← طلبات التجار' : '← Trader requests'}
            </button>
          </div>
        </div>

        {err && <p style={{ color: 'var(--ac-danger)', fontSize: 13 }}>{err}</p>}
        {d?.missing && <div className="ac-empty">{isAr ? 'هذا الطلب غير موجود.' : 'No such request.'}</div>}

        {/* Admin edit (2026-10-12): fix a request the trader left without
            photos, a category or a clear description. Through
            admin_update_request, audited; reloads the page on save. */}
        {d && !d.missing && r && (
          <div style={{ marginBottom: 12 }}>
            <RequestEditPanel request={r} signed={d.attachments?.signed || {}} isAr={isAr} flash={flash} onSaved={load} />
          </div>
        )}

        {d && !d.missing && (
          <>
            {/* 0 ── how far this request has got ──────────────────────────
                The admin page had a status CHIP and nothing else, so "where is
                this in its life" had to be reconstructed from which panels
                happened to render. This is the trader's own bar, imported from
                src/lib/requestTimeline.js — same six stages, same order, same
                labels, same requests.status. Not a copy: if the trader's bar
                changes, this changes with it.

                READ-ONLY, deliberately. Nothing here is clickable and nothing
                here writes. An admin moving a request through its stages by
                poking a progress bar is not a feature anybody asked for, and
                every stage transition already has an owner elsewhere.

                A cancelled request renders it DIMMED — the component does that
                itself, from requests.status. */}
            <div className="ac-card" style={{ padding: '14px 20px 18px', marginBottom: 18 }}>
              <p className="ac-flabel" style={{ margin: '0 0 2px' }}>
                {isAr ? 'مسار الطلب' : 'Request stage'}
              </p>
              <RequestTimeline request={r} lang={lang} />
            </div>

            {/* 1 ── what the factory reads */}
            <div className="ac-card" style={{ padding: 20, marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                <strong style={{ fontSize: 14 }}>{isAr ? 'ما يقرأه المصنع' : 'What the factory reads'}</strong>
                {/* The only faithful preview of a page is that page. When an
                    invite exists this opens the real one rather than asking the
                    admin to trust a rebuild of it. */}
                {d.invites[0]?.slug && (
                  <button className="ac-btn ac-btn-sm" onClick={() => window.open(`/f/${d.invites[0].slug}`, '_blank', 'noopener')}>
                    {isAr ? 'افتح الصفحة الحقيقية ↗' : 'Open the real page ↗'}
                  </button>
                )}
              </div>

              {/* ⚠️ THE ENGLISH TITLE ABOVE IS DELIBERATE, and this line is why
                  it now says so on screen instead of only in the code.
                  `title` comes from d.factoryView, built in
                  consoleRequestDetail.js:123 as title_en || title_zh ||
                  title_ar — the order /f/ prefers. An admin reading this block
                  is reading the factory's copy, so translating it here would
                  make the preview a lie about the thing it previews. */}
              <p style={{ fontSize: 11.5, color: 'var(--ac-faint)', margin: '0 0 12px', lineHeight: 1.7 }}>
                {isAr
                  ? 'العنوان والنص هنا بلغة المصنع (إنجليزي ثم صيني)، لا بلغتك — هذه نسخة المصنع كما سيقرأها.'
                  : "Title and text here are in the factory's language (English, then Chinese), not yours — this is the factory's copy as they will read it."}
              </p>

              <p style={{ fontSize: 14, lineHeight: 1.9, color: 'var(--ac-ink)', whiteSpace: 'pre-wrap', margin: '0 0 12px' }}>
                {/* «brief» was a Latin word dropped mid-Arabic sentence. «ملخّص»
                    is what the rest of the product calls it. */}
                {fv.brief || fv.description || (isAr ? '— لا وصف ولا ملخّص. المصنع سيرى العنوان والكمية فقط.' : '— No brief and no description. The factory will see only the title and quantity.')}
              </p>

              {/* The trader's ORIGINAL text, before contact details were masked
                  at save (20261011223741). Suppliers, the translator and every
                  email only ever get the masked copy above; this is the one
                  place it is shown whole — the admin still sees everything
                  (owner, 2026-10-11). Absent unless something was masked. */}
              {d.textOriginals?.length > 0 && (
                <div style={{ margin: '0 0 14px', padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(196,138,46,0.35)', background: 'rgba(196,138,46,0.06)' }}>
                  <p className="ac-badge warn" style={{ margin: '0 0 8px' }}>
                    {isAr ? 'أُخفيت معلومات تواصل من نص التاجر — المورّد لا يراها' : 'Contact details were masked from the trader\'s text — suppliers do not see them'}
                  </p>
                  {d.textOriginals.map((o) => (
                    <div key={o.field} style={{ marginTop: 6 }}>
                      <p style={{ fontSize: 11.5, color: 'var(--ac-faint)', margin: 0 }}>
                        {(MASKED_FIELD_LABEL[o.field] || {})[isAr ? 'ar' : 'en'] || o.field}
                        {isAr ? ' — النص الأصلي' : ' — original'}
                      </p>
                      <p style={{ fontSize: 13.5, lineHeight: 1.8, color: 'var(--ac-ink)', whiteSpace: 'pre-wrap', margin: '2px 0 0' }}>{o.original}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* The trader's terms, in the admin's language, through the
                  supplier builder: quantity + unit, target price, payment plan,
                  the delivery ask naming the Incoterm, sample, destination,
                  timeframe. Target price and payment plan reach /f/ only once
                  the proposed get_factory_invite change is applied — until
                  then this preview shows two rows that page does not. */}
              {supplierTermRows(fv, isAr ? 'ar' : 'en').map((row) => (
                <Row key={row.key} k={row.label} v={row.value} />
              ))}
              <Row k={isAr ? 'التخصيص' : 'Customization'}
                v={(fv.customization_types || []).map((c) => chipLabel(CUSTOMIZATION_CHIPS, c)).filter(Boolean).join('، ') || null} />
              <Row k={isAr ? 'آخر موعد للرد' : 'Response deadline'} v={fv.deadline ? fmt(fv.deadline, isAr) : null} />
              <Row k={isAr ? 'التاجر' : 'Trader'}
                v={d.buyer ? `${nf(d.buyer.company_name) || nf(d.buyer.full_name) || '—'} · ${[nf(d.buyer.city), nf(d.buyer.country)].filter(Boolean).join('، ')}` : null} />

              {d.attachments.paths.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <p style={{ fontSize: 12, color: 'var(--ac-faint)', margin: '0 0 8px' }}>
                    {isAr ? `المرفقات (${d.attachments.paths.length})` : `Attachments (${d.attachments.paths.length})`}
                  </p>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {d.attachments.paths.map((p) => (
                      d.attachments.signed[p]
                        ? <a key={p} href={d.attachments.signed[p]} target="_blank" rel="noopener noreferrer">
                          <img src={d.attachments.signed[p]} alt="" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--ac-line)' }} />
                        </a>
                        : <span key={p} className="ac-badge warn" title={p}>{isAr ? 'تعذّر توقيع مرفق' : 'Could not sign'}</span>
                    ))}
                  </div>
                </div>
              )}

              {r.attachments_missing && (
                <p className="ac-badge warn" style={{ marginTop: 10 }}>
                  {isAr ? 'التاجر أرفق صورًا ولم تُرفع' : 'The trader attached images that never uploaded'}
                </p>
              )}
            </div>

            {/* 2 ── the quotes that came back, INCLUDING the ones the trader
                    cannot see.

                This screen used to reduce the offers query to a count and throw
                the rows away, so a quote held because its supplier is
                unverified appeared nowhere in this console — it was not
                filtered out, it was simply never rendered. Nothing was blocking
                it: offers_select gives an admin every row (20260918172830).

                Held offers are the ones that most need to be here. They are
                waiting on somebody on this side approving the supplier, and
                until that happens the trader sees nothing at all. */}
            <div className="ac-card" style={{ padding: 20, marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                <strong style={{ fontSize: 14 }}>{isAr ? 'العروض' : 'Offers'}</strong>
                <span style={{ fontSize: 12, color: 'var(--ac-faint)' }}>({d.offers.length})</span>
              </div>

              {d.offers.length === 0 ? (
                <p style={{ fontSize: 12.5, color: 'var(--ac-faint)', margin: 0 }}>
                  {isAr ? 'لا عروض على هذا الطلب بعد.' : 'No offers on this request yet.'}
                </p>
              ) : d.offers.map((o) => {
                // The request is passed, not just the offer: on a curated
                // request an 'unverified_supplier' row is still never released
                // by reveal_quotes_on_supplier_approval, so it must not offer
                // an approve action that would change nothing.
                const chip = offerVisibilityChip(o, r, lang);
                const who = nf(o.supplier?.company_name) || nf(o.supplier?.full_name)
                  || (isAr ? 'مورّد' : 'Supplier');
                return (
                  <div key={o.id} style={{ borderTop: '1px solid var(--ac-line-2)', padding: '10px 0' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: 13 }}>{who}</strong>
                      {chip && (
                        <span className={`ac-badge ${chip.tone}`}><span className="dot" />{chip.label}</span>
                      )}
                      {/* A link ONLY when there is a file to open.

                          Two conditions, and both are necessary:
                          • chip.action — the offer is held on the supplier's
                            status, not raw on a curated request.
                          • bucketOf(...) === 'ready' — the supplier is in the
                            Reviews queue WITH a licence attached. That is the
                            existing paired predicate (consoleVerification.js,
                            isReviewable/fetchReadyCount), not a third
                            expression of it.

                          A supplier at 'registered', or in review with nothing
                          filed, gets NO button. The chip still says the quote
                          is held, so the situation stays visible — but we do
                          not offer an action that opens an empty file. Owner's
                          decision, 2026-09-20: no nudge, no email, nothing
                          else to build.

                          ⚠️ THE TARGET IS /admin2/verification/:profileId, NOT
                          /admin2/suppliers/:id. The latter is the FACTORY page
                          — ConsoleSupplierDetail calls fetchFactory() against
                          factory_directory and renders 'المورّد غير موجود' for
                          anything else, so a profiles.id there is always a dead
                          end. This button pointed at it until 2026-09-20. */}
                      {chip?.action && o.supplier_id && bucketOf(o.supplier || {}) === 'ready' && (
                        <button className="ac-btn ac-btn-sm"
                          onClick={() => nav(`/admin2/verification/${o.supplier_id}`)}>
                          {isAr ? 'راجع المورّد ←' : 'Review supplier →'}
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 12, marginTop: 5, fontSize: 12, color: 'var(--ac-faint)', flexWrap: 'wrap' }}>
                      {o.price != null && <span dir="ltr">{o.price} {nf(o.currency)}</span>}
                      {/* Admins see everything (owner, 2026-09-29): the supplier's
                          own figure above, and here the rate locked at acceptance
                          with the unit price in SAR. Traders never see either. */}
                      {o.price != null && String(o.currency || 'SAR').toUpperCase() !== 'SAR' && (
                        o.sar_rate != null
                          ? <span dir="ltr" title={isAr ? 'السعر المثبّت عند القبول' : 'Rate locked at acceptance'}>
                              ≈ {(Number(o.price) * Number(o.sar_rate)).toFixed(2)} SAR · @{Number(o.sar_rate)} {isAr ? '(مثبّت)' : '(locked)'}
                            </span>
                          : <span>{isAr ? 'يُثبَّت السعر عند القبول' : 'Rate locks on acceptance'}</span>
                      )}
                      {o.moq && <span>{isAr ? 'أقل كمية' : 'MOQ'}: {nf(o.moq)}</span>}
                      {o.delivery_days != null && <span>{isAr ? 'التسليم' : 'Delivery'}: {o.delivery_days}{isAr ? ' يوم' : 'd'}</span>}
                      {/* offers.status is the TRADER's decision, never the
                          visibility gate — the two were deliberately kept on
                          separate columns (golden rule 2). Labelled so nobody
                          reads "pending" as "pending review by us". */}
                      <span title={isAr ? 'قرار التاجر' : "The trader's decision"}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {isAr ? 'قرار التاجر' : 'Trader'}:
                        {/* Was the raw column in plain grey text: «قرار التاجر:
                            accepted», styled identically on the offer the trader
                            CHOSE and on the ones they turned down — the most
                            important fact on this list, invisible until read.

                            OFFER_STATUS is the map DashboardSupplier and
                            RequestDetail already read; the ar-then-raw fallback
                            is the same chain RequestDetail:188 uses. The tone
                            comes from the same module, so the word and its
                            colour cannot drift. */}
                        {o.status ? (
                          <span className={`ac-badge ${offerStatusTone(o.status)}`}>
                            <span className="dot" />
                            {OFFER_STATUS[lang]?.[o.status] || OFFER_STATUS.ar[o.status] || o.status}
                          </span>
                        ) : '—'}
                      </span>
                      <span>{fmt(o.created_at, isAr)}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 3 + 4 + 5 ── who has it, and who else should: suppliers and
                    factories in one panel */}
            <div className="ac-card" style={{ padding: 20, marginBottom: 18 }}>
              <DistributePanel
                requestId={r.id}
                request={r}
                offerable={!!d.offerable}
                // The RAW column: `r` here IS the requests row. Used only to
                // word the refusal, never to decide it.
                rawStatus={r.status}
                isAr={isAr}
                flash={flash}
                initialCategory={cat?.key}
                onOpenInvite={(factoryId) => setInvite({ factoryId, request: r })}
              />
            </div>

            {/* 5 ── routing to self-registered suppliers lives INSIDE the
                    DistributePanel above since 2026-10-12. It was a separate
                    RoutePanel card, and staff read the split as "the
                    distribute screen does not show every supplier". */}

            {/* 6 ── the deadline, which gates 7 */}
            <div style={{ marginBottom: 16 }}>
              <DeadlineEditor
                requestId={r.id}
                deadline={r.response_deadline}
                isAr={isAr}
                flash={flash}
                onSaved={load}
              />
            </div>

            {/* 7 ── the public link */}
            <PublicLinkPanel requestId={r.id} offerable={!!d.offerable} isAr={isAr} flash={flash} />
          </>
        )}
      </div>

      {invite && (
        <InviteModal
          factoryId={invite.factoryId}
          request={invite.request}
          isAr={isAr}
          flash={flash}
          onClose={() => { setInvite(null); load(); }}
        />
      )}
      {toast && <div className="ac-toast">{toast}</div>}
    </ConsoleShell>
  );
}
